import React, { ReactNode, FC } from 'react';
import { useHistory } from 'app/components/router/History';
import { History } from 'app/services/history';
import { useQuery } from 'app/components/Contexts';
import { finishAttachExternalOauth } from 'app/services/oauth/attach';
import { InvalidResultError } from 'app/services/oauth/errors';
import { ContinuationMissingError } from 'app/services/oauth/Continuation';
import { LoginRoute } from 'app/constants/Routes';
import { getAuthUser } from 'app/models/auth/selectors';
import { connect } from 'react-redux';
import { User } from 'app/constants/schemas/User';
import { getDestinationRouteForUser } from 'app/components/auth/UserSensitiveLink';
import { BrandContextValue, useBrand } from '../OrganizationContext';
import { getBrandDomainLocation } from 'app/utils/brand';

interface IProps {
  children: (errorResult: {error: unknown}) => ReactNode;
  history: History;
  query: { [index: string]: unknown };
  brand: BrandContextValue;
  user: User;
}

interface IState {
  error: unknown;
}

class OauthAttachCallback extends React.Component<IProps, IState> {
  public constructor(initialProps: IProps) {
    super(initialProps);
    this.state = { error: null };
  }

  public componentDidMount() {
    void this.onContinueAttach();
  }

  public render() {
    const { error } = this.state;
    return this.props.children({ error });
  }

  private async onContinueAttach() {
    const { history, query, brand, user } = this.props;

    try {
      const { redirectUriReference } = finishAttachExternalOauth(query);
      if (redirectUriReference) {
        history.replace(redirectUriReference);
      }
    } catch (error) {
      if (
        error instanceof ContinuationMissingError ||
        error instanceof InvalidResultError
      ) {
        // Probably not our backend. Redirect to somewhere neutral.
        if (user) {
          const defaultDomainLocation = getBrandDomainLocation(brand.defaultDomain);
          history.smartReplace(getDestinationRouteForUser(user, brand, defaultDomainLocation));
        } else {
          history.smartReplace(LoginRoute.getPathname());
        }
      } else {
        this.setState({ error });
      }
    }
  }
}

const ConnectedOauthAttachCallback = connect(
  (state) => ({
    user: getAuthUser(state),
  }),
)(OauthAttachCallback);

const OauthAttachCallbackInjectDependencies: FC<Pick<IProps, 'children'>> = (props) => {
  const history = useHistory();
  const query = useQuery();
  const brand = useBrand();
  return (
    <ConnectedOauthAttachCallback
      history={history}
      query={query}
      brand={brand}
      {...props}
    />
  );
};

export default OauthAttachCallbackInjectDependencies;
